import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { iFullStoreState } from '../../../shared/interfaces';
import { useReplayControl } from '../../gmap/ReplyContext';
import styles from './MapProgressSlider.module.scss';

interface MapProgressSliderProps {
    isVisible?: boolean;
    forceShow?: boolean;
}

const MapProgressSlider: React.FC<MapProgressSliderProps> = ({ isVisible = true, forceShow = false }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragValue, setDragValue] = useState(0);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState(0);

    const activeDeviceId = useSelector<iFullStoreState, string | null>(state => state.replay?.activeDeviceId);
    const replayPlaying = useSelector<iFullStoreState, { [deviceId: string]: boolean }>(state => state.replay?.isPlaying || {});

    const devicesTripsPoints = useSelector<iFullStoreState, any>(state => state.devicesTripsPoints);
    const hasVisibleTrips = () => {
        if (!devicesTripsPoints || !devicesTripsPoints.some) return false;
        return devicesTripsPoints.some((deviceTrips: any) => {
            const reportTripsIds = deviceTrips.get('reportTripsIds');
            return reportTripsIds && reportTripsIds.some((show: boolean) => show === true);
        });
    };

    const { getCurrentPointInfo, navigateToNextPoint, navigateToPrevPoint } = useReplayControl();

    const scrubToPercent = useCallback((deviceId: string, percent: number) => {
        const pointInfo = getCurrentPointInfo(deviceId);
        if (!pointInfo) return;

        const targetIndex = Math.round((percent / 100) * (pointInfo.totalPoints - 1));
        const currentIndex = pointInfo.currentIndex;

        // Only navigate if there's a significant difference to avoid micro-movements
        const steps = targetIndex - currentIndex;
        if (Math.abs(steps) > 0) {
            if (steps > 0) {
                // Go forward
                for (let i = 0; i < steps; i++) {
                    navigateToNextPoint(deviceId);
                }
            } else {
                // Go backward
                for (let i = 0; i < Math.abs(steps); i++) {
                    navigateToPrevPoint(deviceId);
                }
            }
        }
    }, [getCurrentPointInfo, navigateToNextPoint, navigateToPrevPoint]);

    const pointInfo = activeDeviceId ? getCurrentPointInfo(activeDeviceId) : null;
    const isPlaying = activeDeviceId ? (replayPlaying[activeDeviceId] || false) : false;

    // Calculate progress percentage - update in real-time based on current replay point
    const progressPercent = useMemo(() => {
        if (!pointInfo || pointInfo.totalPoints <= 1) return 0;

        // Use the current index from pointInfo which updates in real-time during replay
        return Math.min(100, Math.max(0, (pointInfo.currentIndex / (pointInfo.totalPoints - 1)) * 100));
    }, [pointInfo?.currentIndex, pointInfo?.totalPoints]);

    const displayPercent = isDragging ? dragValue : progressPercent;

    // Keep drag value in sync with progress when not manually dragging
    useEffect(() => {
        if (!isDragging) {
            setDragValue(progressPercent);
        }
    }, [progressPercent, isDragging]);


    const handleSliderChange = useCallback((value: number) => {
        if (!activeDeviceId || !pointInfo) return;

        setDragValue(value);

        scrubToPercent(activeDeviceId, value);
    }, [activeDeviceId, pointInfo, scrubToPercent]);

    const handleMouseDown = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleMouseUp = useCallback(() => {
        if (isDragging && activeDeviceId) {
            // Apply the final scrub position
            scrubToPercent(activeDeviceId, dragValue);
        }
        setIsDragging(false);
    }, [isDragging, activeDeviceId, dragValue, scrubToPercent]);

    const handleProgressBarMouseEnter = useCallback(() => {
        setShowTooltip(true);
    }, []);

    const handleProgressBarMouseLeave = useCallback(() => {
        if (!isDragging) {
            setShowTooltip(false);
        }
    }, [isDragging]);

    const handleProgressBarMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const percent = ((e.clientX - rect.left) / rect.width) * 100;
        const clampedPercent = Math.max(0, Math.min(100, percent));
        setTooltipPosition(clampedPercent);
    }, []);

    if (!forceShow && (
        !isVisible ||
        !hasVisibleTrips() ||
        !activeDeviceId ||
        !pointInfo ||
        pointInfo.totalPoints < 1
    )) {
        return null;
    }

    const mockPointInfo = {
        currentIndex: 25,
        totalPoints: 100,
        hasNext: true,
        hasPrev: true
    };

    const finalPointInfo = forceShow && !pointInfo ? mockPointInfo : pointInfo;

    const safePointInfo = {
        currentIndex: finalPointInfo?.currentIndex || 0,
        totalPoints: finalPointInfo?.totalPoints || 1,
        hasNext: finalPointInfo?.hasNext || false,
        hasPrev: finalPointInfo?.hasPrev || false
    };

    const getTooltipContent = (percent: number) => {
        const targetIndex = Math.round((percent / 100) * (safePointInfo.totalPoints - 1));
        return `Trip Point ${targetIndex + 1}`;
    };

    return (
        <div
            className={styles.progressSliderContainer}
        >
            <div
                className={styles.sliderWrapper}
                onMouseEnter={handleProgressBarMouseEnter}
                onMouseLeave={handleProgressBarMouseLeave}
                onMouseMove={handleProgressBarMouseMove}
            >
                    <div
                        className={styles.progressBar}
                    >
                        <div
                            className={`${styles.progressFill} ${isPlaying ? styles.playing : ''}`}
                            style={{ width: `${displayPercent}%` }}
                        />
                        <div
                            className={`${styles.progressIndicator} ${isPlaying ? styles.playing : ''} ${isDragging ? styles.manuallySet : ''} ${(isDragging || !showTooltip) ? styles.showTooltip : ''}`}
                            style={{ left: `${displayPercent}%` }}
                            data-tooltip={`Trip Point ${safePointInfo.currentIndex + 1}`}
                        />
                        {/* Hover tooltip - invisible element positioned at cursor, only shows tooltip */}
                        {showTooltip && !isDragging && (
                            <div
                                className={`${styles.hoverTooltip} ${styles.showTooltip}`}
                                style={{ left: `${tooltipPosition}%` }}
                                data-tooltip={getTooltipContent(tooltipPosition)}
                            />
                        )}
                    </div>

                    <input
                        type="range"
                        min={0}
                        max={100}
                        step={100 / (safePointInfo.totalPoints - 1 || 1)}
                        value={displayPercent}
                        onChange={(e) => handleSliderChange(parseFloat(e.target.value))}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onTouchStart={handleMouseDown}
                        onTouchEnd={handleMouseUp}
                        className={styles.slider}
                        disabled={false}                />
            </div>
        </div>
    );
};

export default MapProgressSlider;
